import React, { useCallback, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { observer } from 'mobx-react';
import { useKeycloak } from '@react-keycloak/web';
import classnames from 'classnames';
import { Icon } from 'ksnm-common-ui/lib/Icon';
import type { Dispatch } from 'react';

import type { MenuRoute } from '@stores/designMenuStore';

import { DesignSubMenuChildren } from './DesignSubMenuChildren';

import styles from './DesignSubMenuItem.module.scss';

interface DesignSubMenuItemInterface {
  item: MenuRoute;
  setOpen: Dispatch<string | number | undefined>;
  open: string | number | undefined;
}

function DesignSubMenuItemComponent({ item, setOpen, open }: DesignSubMenuItemInterface): JSX.Element {
  const { keycloak } = useKeycloak();
  const { logout } = keycloak;
  const {
    location: { pathname },
    push,
  } = useHistory();

  const children = useMemo(() => (item.routes && item.routes.length > 0 ? item.routes : null), [item]);

  const onClick = useCallback(() => {
    if (item.path === 'logout') {
      logout();
      return;
    }

    if (item.link) {
      window.open(item.path, '_blank');
      return;
    }

    if (item.path) {
      if (item.path !== pathname) {
        push(item.path);
      }
    }
    if (children) {
      setOpen(open === item.title ? undefined : item.title);
    }
  }, [item, children, logout, pathname, push, setOpen, open]);

  const mainPath = useMemo(() => pathname.split('/').slice(0, 2).join('/'), [pathname]);

  return (
    <>
      <div
        className={classnames(styles.item, {
          [styles.disabled]: !item.path && !item.routes,
          [styles.isOpen]: item.path === mainPath,
        })}
        role="button"
        tabIndex={-1}
        onClick={onClick}
        onKeyDown={onClick}
      >
        <div className={styles.text}>{item.title ?? ''}</div>
        <div className={styles.topRight}>
          <Icon name="upCornerMenu" fill="white" />
        </div>
        <div className={styles.bottomRight}>
          <Icon name="downCornerMenu" fill="white" />
        </div>
        {children && (
          <div className={classnames(styles.arrow, { [styles.open]: open === item.title })}>
            <Icon name="sideBarArrow" />
          </div>
        )}
      </div>
      {children && <DesignSubMenuChildren items={children} open={open === item.title} />}
    </>
  );
}

export const DesignSubMenuItem = observer(DesignSubMenuItemComponent);
